import { useCallback, useRef } from 'react';
import { toast } from '@/utils/toast';
import {
  FileWithValidation,
  UseUploadHandlersReturn,
  UseFileValidationReturn,
  ValidationStep,
} from '../types';

interface UseUploadHandlersProps {
  fileValidation: UseFileValidationReturn;
  uploadQueue: {
    addFiles: (files: File[]) => void;
    startUpload: () => void;
  };
  uploadQueueWidget: {
    openWidget: () => void;
  };
  onShowUploadModal?: (show: boolean) => void;
  onShowValidationModal?: (show: boolean) => void;
  onFilesUploaded?: (uploaded: boolean) => void;
  onUploadedFileIds?: (ids: string[]) => void;
  onStepChange?: (step: ValidationStep) => void;
}

export const useUploadHandlers = ({
  fileValidation,
  uploadQueue,
  uploadQueueWidget,
  onShowUploadModal,
  onShowValidationModal,
  onFilesUploaded,
  onUploadedFileIds,
  onStepChange,
}: UseUploadHandlersProps): UseUploadHandlersReturn => {
  // Refs for file inputs
  const folderInputRef = useRef<HTMLInputElement>(null);
  const filesInputRef = useRef<HTMLInputElement>(null);
  const folderInputRefActions = useRef<HTMLInputElement>(null);
  const filesInputRefActions = useRef<HTMLInputElement>(null);

  // File selection handlers for initial empty state
  const handleSelectFolder = useCallback(() => {
    folderInputRef.current?.click();
  }, []);

  const handleSelectFiles = useCallback(() => {
    filesInputRef.current?.click();
  }, []);

  const handleFolderChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        fileValidation.addFiles(event.target.files);
        event.target.value = '';
      }
    },
    [fileValidation]
  );

  const handleFilesChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        fileValidation.addFiles(event.target.files);
        event.target.value = '';
      }
    },
    [fileValidation]
  );

  // Add more files handlers (when files already loaded)
  const handleAddFolder = useCallback(() => {
    folderInputRefActions.current?.click();
  }, []);

  const handleAddMoreFiles = useCallback(() => {
    filesInputRefActions.current?.click();
  }, []);

  const handleFolderChangeActions = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        fileValidation.addFiles(event.target.files);
        event.target.value = '';
      }
    },
    [fileValidation]
  );

  const handleFilesChangeActions = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        fileValidation.addFiles(event.target.files);
        event.target.value = '';
      }
    },
    [fileValidation]
  );

  // File management handlers
  const handleRemoveFile = useCallback(
    (file: FileWithValidation) => {
      fileValidation.removeFile(file);
    },
    [fileValidation]
  );

  const handleRetryValidation = useCallback(
    async (file: FileWithValidation) => {
      await fileValidation.retryValidation(file);
    },
    [fileValidation]
  );

  const handleClearAllFiles = useCallback(() => {
    fileValidation.clearAllFiles();
    onFilesUploaded?.(false);
    onUploadedFileIds?.([]);
  }, [fileValidation, onFilesUploaded, onUploadedFileIds]);

  // Validation handlers
  const handleValidateAll = useCallback(() => {
    onShowValidationModal?.(true);
    onStepChange?.('server-validation');
  }, [fileValidation, onShowValidationModal, onStepChange]);

  const handleStartValidation = useCallback(async () => {
    onStepChange?.('server-validation');
    await fileValidation.validateFilesMeta();
  }, [fileValidation, onStepChange]);

  // Upload handlers
  const handleUploadFiles = useCallback(() => {
    const readyFiles = fileValidation.getFilesReadyForUpload();

    if (readyFiles.length === 0) {
      toast.error('No files ready for upload', {
        description: 'Please validate files first or resolve conflicts',
      });
      return;
    }

    onShowUploadModal?.(true);
  }, [fileValidation, onShowUploadModal]);

  const handleConfirmUpload = useCallback(() => {
    const readyFiles = fileValidation.getFilesReadyForUpload();
    const filesToUpload = readyFiles.map(
      fileWithValidation => fileWithValidation.file
    );

    // Add files to upload queue
    uploadQueue.addFiles(filesToUpload);
    uploadQueue.startUpload();

    // Track uploaded file IDs
    const fileIds = filesToUpload.map(
      file => `${file.name}-${file.size}-${file.lastModified}`
    );
    onUploadedFileIds?.(fileIds);
    onFilesUploaded?.(true);

    // Show success notification
    toast.success(`Files added to upload queue`, {
      description: `${filesToUpload.length} files added and upload started`,
      action: {
        label: 'View Queue',
        onClick: uploadQueueWidget.openWidget,
      },
    });

    // Update step to upload complete
    onStepChange?.('upload-complete');
    onShowUploadModal?.(false);
  }, [
    fileValidation,
    uploadQueue,
    uploadQueueWidget,
    onFilesUploaded,
    onUploadedFileIds,
    onStepChange,
    onShowUploadModal,
  ]);

  return {
    // Refs for JSX
    folderInputRef,
    filesInputRef,
    folderInputRefActions,
    filesInputRefActions,

    // Initial file selection handlers
    handleSelectFolder,
    handleSelectFiles,
    handleFolderChange,
    handleFilesChange,

    // Add more files handlers
    handleAddFolder,
    handleAddMoreFiles,
    handleFolderChangeActions,
    handleFilesChangeActions,

    // File management handlers
    handleRemoveFile,
    handleRetryValidation,
    handleClearAllFiles,

    // Validation handlers
    handleValidateAll,
    handleStartValidation,

    // Upload handlers
    handleUploadFiles,
    handleConfirmUpload,
  };
};
